import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, ClickAwayListener, Fade, Grid, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemText, Paper, Popper, TextField, Typography, useTheme } from "@mui/material";
import moment from 'moment';
import 'moment/locale/de';
import { useEffect, useRef, useState } from 'react';

//selection is parsed three times to allow focus on second iteration for better initial state and value setting
const twentyfourhours = ["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "00"]
const minutes = ["00", "15", "30", "45", "00", "15", "30", "45", "00", "15", "30", "45", "00"]

const WheelTimePicker = ({ label, value, setValue, error, showIcon, close, disable, setUpdated }) => {

    const theme = useTheme();

    const [date, setDate] = useState(moment());
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [hourIndex, setHourIndex] = useState(24);
    const [minuteIndex, setMinuteIndex] = useState(4);
    const [safariFix, setSafariFix] = useState(true);

    const hourRef = useRef([]);
    const minuteRef = useRef([]);

    useEffect(() => {
        const newDate = moment(value);
        const indexForHours = twentyfourhours.findIndex(v => v === newDate.hours().toString().padStart(2, '0')) + 24;
        const indexForMinutes = minutes.findIndex(v => v === newDate.minutes().toString().padStart(2, '0')) + 4;
        setHourIndex(indexForHours);
        setMinuteIndex(indexForMinutes);
        setDate(newDate);
        if (close) {
            setOpen(false);
        }
        // console.log(`label: ${label}\nDateState: ${date.format("HH:mm")}\nValueProps: ${value.format("HH:mm")}`);
        //eslint-disable-next-line
    }, [value, close])

    const setHour = (hour) => {
        let index = twentyfourhours.findIndex(v => v === hour);
        //updating the index to allow for centering of list for all values
        if (index <= 1) {
            index = index + 24;
        }
        //parsing into new object to trigger stateful refesh
        const newDate = moment({ ...date });
        newDate.set("hours", hour);
        setDate(newDate);
        if (setUpdated) {
            setUpdated(true);
        }
        hourRef.current[index]?.scrollIntoView({
            block: "center",
            // behavior: "smooth"
        });
    }

    const setMinute = (minute) => {
        let index = minutes.findIndex(v => v === minute);
        if (index <= 1) {
            index = index + 4;

        }
        const newDate = moment({ ...date });
        newDate.set("minutes", minute);
        setDate(newDate);
        if (date.hours() !== moment().hours()) {
            setOpen(false);
            setAnchorEl(null);
            setValue(newDate);
        };
        if (setUpdated) {
            setUpdated(true);
        }
        minuteRef.current[index]?.scrollIntoView({
            block: "center",
            // behavior: "smooth"
        });
    }

    const handleScrollHour = (event) => {
        if (event.target.scrollTop === 0) {
            hourRef.current[24]?.scrollIntoView({ block: "start" });
        }
        if (Math.trunc(event.target.scrollHeight - event.target.scrollTop) === event.target.clientHeight) {
            hourRef.current[24]?.scrollIntoView({ block: "end" });
        }
    }

    const handleScrollMinute = (event) => {
        if (event.target.scrollTop === 0) {
            minuteRef.current[4]?.scrollIntoView({ block: "start" });
        }
        if (Math.trunc(event.target.scrollHeight - event.target.scrollTop) === event.target.clientHeight) {
            minuteRef.current[4]?.scrollIntoView({ block: "end" });
        }
    }

    return (
        <ClickAwayListener onClickAway={() => { if (open) { setOpen(false); setAnchorEl(null); setValue(date); setSafariFix(true); } }}>
            <Box>
                <TextField
                    variant="outlined"
                    label={label}
                    value={moment(date).format("HH:mm")}
                    onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                        if (open) {
                            setOpen(false);
                            setSafariFix(false);
                        } else {
                            setOpen(true);
                            setSafariFix(true);
                        }
                    }}
                    inputProps={{
                        readOnly: true
                    }}
                    focused={open}
                    error={error}
                    InputProps={{
                        endAdornment: showIcon ? (
                            <InputAdornment position="end">
                                <IconButton edge="end">
                                    <AccessTimeIcon />
                                </IconButton>
                            </InputAdornment>)
                            : null
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={disable}
                />
                <Popper
                    open={open}
                    onFocus={() => {
                        if (safariFix) {
                            hourRef.current[hourIndex]?.scrollIntoView({
                                block: "center",
                                // behavior: "smooth"
                            });
                            minuteRef.current[minuteIndex]?.scrollIntoView({
                                block: "center",
                                // behavior: "smooth"
                            });
                            setSafariFix(false);
                        }
                    }}
                    anchorEl={anchorEl}
                    transition
                    sx={{
                        zIndex: theme.zIndex.tooltip + 1
                    }}
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps}>
                            <Paper elevation={3} sx={{ backgroundImage: "none" }}>
                                <Grid container sx={{ py: 2, px: 2.75 }}>
                                    <Grid item sx={{ pr: 1.5 }}>
                                        <List
                                            dense
                                            onScroll={handleScrollHour}
                                            sx={{
                                                p: 0,
                                                overflow: "auto",
                                                maxHeight: 103,
                                                overscrollBehavior: "none",
                                                "&::-webkit-scrollbar": { width: 0 }
                                            }}
                                        >
                                            {twentyfourhours.map((h, i) => (
                                                <ListItem key={`hour-${h}-${i}`} ref={(node) => (hourRef.current[i] = node)} sx={{ p: 0, borderRadius: 6, bgcolor: date.hours() === parseInt(h) ? "primary.main" : "" }}>
                                                    <ListItemButton onClick={() => { setHour(h) }} autoFocus={i === hourIndex} sx={{ py: 0, px: 3, borderRadius: 6 }}>
                                                        <ListItemText>
                                                            <Typography color={date.hours() === parseInt(h) ? "primary.contrastText" : "text.primary"} fontSize={12}>{h}</Typography>
                                                        </ListItemText>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                    <Grid item>
                                        <List
                                            dense
                                            onScroll={handleScrollMinute}
                                            sx={{
                                                p: 0,
                                                overflow: "auto",
                                                maxHeight: 103,
                                                overscrollBehavior: "none",
                                                "&::-webkit-scrollbar": { width: 0 }
                                            }}
                                        >
                                            {minutes.map((m, i) => (
                                                <ListItem key={`minute-${m}-${i}`} ref={(node) => (minuteRef.current[i] = node)} sx={{ p: 0, borderRadius: 6, bgcolor: date.minutes() === parseInt(m) ? "primary.main" : "" }}>
                                                    <ListItemButton onClick={() => { setMinute(m) }} autoFocus={i === minuteIndex} sx={{ py: 0, px: 3, borderRadius: 6 }}>
                                                        <ListItemText>
                                                            <Typography color={date.minutes() === parseInt(m) ? "primary.contrastText" : "text.primary"} fontSize={12}>{m}</Typography>
                                                        </ListItemText>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Fade>
                    )}
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default WheelTimePicker;